import { html, nothing, svg, type PropertyValues } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { ModelSectionBase } from './model-section-base';
import { getLocale, t } from '@vitesse/base-ui/i18n';
import { statusBadge } from '@vitesse/base-ui/components/templates';
import { isModelActive } from './model-status';
import { machineRows, type ArrayRow, type MachineCell, type MachineRow } from './machine-rows';
import { machineGraph, type GraphMachine, type GraphOutput, type MachineGraph } from './machine-graph';
import { listSep } from './statechart-replay-model';
import type { ModelChildPort } from '../../../core/domain/model';
import type { PillTabItem } from '@vitesse/base-ui/elements/vitesse-pill-tabs';
import '@vitesse/base-ui/elements/vitesse-icon';
import '@vitesse/base-ui/elements/vitesse-pill-tabs';

/*
 * <vitesse-model-machines> — a group's machines (Part 7 of docs/hierarchical-models-step-1-plan.md,
 * Decision 3), in 2 views that a pill bar switches between.
 *
 * LIST: the bindings panel's row system — one bordered box, hairline-separated 34px rows, one line
 * per machine, the root first. A row is a sentence in one mono size: the name lit, the template and
 * the wiring in the secondary tone, the live state as a badge on the right. The state comes from the
 * runtime's `machines`, each by its path, not from `current_state`, which is the root's alone: two
 * machines can share a state id. An array child (Step 4) is 1 row with the number of its machines
 * that run, and its faults. The row opens to show each machine: a row for each in a small array, a
 * grid of small cells in a large one. The machines are rendered only while the row is open.
 * `machine-rows.ts` builds the rows.
 *
 * DIAGRAM: the same group drawn as the flows canvas draws its graph — the same node card, grey
 * wires that flow blue while the model runs. The outside inputs are on the left, the
 * machines in the middle (the children that the root reads, the root, the children
 * that the root drives), and the PLC outputs and the published outputs on the right.
 * `machine-graph.ts` builds the nodes and the edges. The wires are drawn after the nodes: each render
 * measures the nodes and draws a curve from one to the other. The whole graph is scaled down to the
 * tile's width, so the group is seen at once; below half size it scrolls instead.
 *
 * The view a person picks is kept in localStorage, for every model. Extends ModelSectionBase, so it
 * is a tile on the model page and can be a dashboard widget.
 */
type MachinesView = 'diagram' | 'list';
const VIEW_KEY = 'vitesse-model-machines-view';
/** A wire's curve, its label and where the label goes, in the canvas's own (unscaled) coordinates. */
interface Wire {
  d: string;
  label: string;
  lx: number;
  ly: number;
  /** A wire inside the running model flows, as a live edge does on the flows canvas. */
  live: boolean;
}
interface Box {
  l: number;
  r: number;
  t: number;
  b: number;
}
/** The smallest the diagram is drawn to fit its tile. Smaller than this, it scrolls. */
const MIN_SCALE = 0.5;

function loadView(): MachinesView {
  try {
    return localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'diagram';
  } catch {
    return 'diagram'; // private mode / storage disabled
  }
}

/**
 * How a wire runs between its 2 nodes. FORWARD: out of the right side of `a`, into the left side of
 * `b`. BACK, to a node on the left: out of the left side, into the right side. SAME, 2 nodes in one
 * column: right side to right side, with a curve that bows out.
 */
type Bend = 'forward' | 'back' | 'same';
const bendOf = (a: Box, b: Box): Bend => (b.l >= a.r - 1 ? 'forward' : b.r <= a.l + 1 ? 'back' : 'same');
const midY = (x: Box): number => (x.t + x.b) / 2;
const f1 = (n: number): string => n.toFixed(1);

/** The space between 2 ends of wires on one side of a node, and the most of the node's height they take. */
const END_GAP = 18;
const END_SPAN = 0.6;
/** The space between 2 wires that go around the same nodes. */
const LANE_GAP = 10;

/**
 * The y of each end on one side of a node. 1 end is at the middle. More ends spread around the
 * middle in the order given, so 2 wires to one node do not meet at one point, and 2 wires between
 * the same 2 nodes run side by side.
 */
function spread(box: Box, n: number): number[] {
  if (n === 1) return [midY(box)];
  const gap = Math.min(END_GAP, ((box.b - box.t) * END_SPAN) / (n - 1));
  const top = midY(box) - (gap * (n - 1)) / 2;
  return Array.from({ length: n }, (_, k) => top + gap * k);
}

/** A wire's curve and its label's place, without the label. */
type Curve = Omit<Wire, 'label' | 'live'>;

/**
 * The curve from (x0, y0) to (x3, y3). A forward wire that would pass under a node goes over it (or
 * under it, if its ends are lower than the node's middle), through 2 points beside the nodes in its
 * way, with its label on that straight part. Any other wire is 1 curve, with its label at the middle:
 * above a forward wire, below a wire that goes back, so 2 wires side by side keep their labels apart.
 */
function curveOf(x0: number, y0: number, x3: number, y3: number, bend: Bend, nodes: Box[], lanes: Map<string, number>): Curve {
  if (bend === 'forward') {
    const lo = Math.min(y0, y3) - 12;
    const hi = Math.max(y0, y3) + 12;
    const inWay = nodes.filter((n) => n.l > x0 + 4 && n.r < x3 - 4 && n.b > lo && n.t < hi);
    if (inWay.length > 0) {
      const left = Math.min(...inWay.map((n) => n.l)) - 10;
      const right = Math.max(...inWay.map((n) => n.r)) + 10;
      const top = Math.min(...inWay.map((n) => n.t));
      const bottom = Math.max(...inWay.map((n) => n.b));
      const over = (y0 + y3) / 2 <= (top + bottom) / 2;
      // Each wire that goes around the same nodes on the same side takes the next lane, so 2 of them
      // do not run on 1 line with 1 label over the other.
      const lane = `${over ? 'over' : 'under'}|${Math.round(left)}|${Math.round(right)}`;
      const k = lanes.get(lane) ?? 0;
      lanes.set(lane, k + 1);
      const y = over ? top - 18 - LANE_GAP * k : bottom + 18 + LANE_GAP * k;
      const dx0 = Math.max(20, (left - x0) / 2);
      const dx3 = Math.max(20, (x3 - right) / 2);
      return {
        d:
          `M ${f1(x0)} ${f1(y0)} C ${f1(x0 + dx0)} ${f1(y0)}, ${f1(left - dx0)} ${f1(y)}, ${f1(left)} ${f1(y)} ` +
          `L ${f1(right)} ${f1(y)} C ${f1(right + dx3)} ${f1(y)}, ${f1(x3 - dx3)} ${f1(y3)}, ${f1(x3)} ${f1(y3)}`,
        lx: (left + right) / 2,
        ly: y - 5,
      };
    }
  }
  let c1: number;
  let c2: number;
  if (bend === 'forward') {
    const dx = Math.max(30, (x3 - x0) / 2);
    c1 = x0 + dx;
    c2 = x3 - dx;
  } else if (bend === 'back') {
    const dx = Math.max(30, (x0 - x3) / 2);
    c1 = x0 - dx;
    c2 = x3 + dx;
  } else {
    c1 = x0 + 48;
    c2 = x3 + 48;
  }
  return {
    d: `M ${f1(x0)} ${f1(y0)} C ${f1(c1)} ${f1(y0)}, ${f1(c2)} ${f1(y3)}, ${f1(x3)} ${f1(y3)}`,
    // The point of the cubic at t = 0.5.
    lx: (x0 + 3 * c1 + 3 * c2 + x3) / 8,
    ly: (y0 + y3) / 2 + (bend === 'back' ? 12 : -5),
  };
}

/** The last segment of an entity path, as a person names it: `In1` for `controllers/plc/symbols/In1`. */
function shortPath(path: string): string {
  if (path.startsWith('controllers/')) {
    const segs = path.split('/');
    return segs.slice(3).join('/') || path;
  }
  if (path.startsWith('models/') || path.startsWith('integrations/')) return path.split('/').at(-1) || path;
  return path; // another child's port, as `<child>/<port>`
}

/**
 * One port as a short phrase: `raw ← In1`, `polarity = "NO"`, `out → Out1`. An input with no wiring
 * is set by the model's main machine, so it reads `cmd ← <the model's name>`. The whole path is the
 * row's tooltip.
 */
function inputText(p: ModelChildPort, main: string): string {
  if (p.constant !== undefined) return `${p.name} = ${p.constant}`;
  if (p.path) return `${p.name} ← ${shortPath(p.path)}`;
  return `${p.name} ← ${main}`;
}
function outputText(p: ModelChildPort): string {
  return p.path ? `${p.name} → ${shortPath(p.path)}` : p.name;
}
/** What a PLC output is set to when its machine stops: "If stopped: off" for `false`. */
function safeText(value: string): string {
  const word = value === 'false' ? t('model.machineSafeOff') : value === 'true' ? t('model.machineSafeOn') : value;
  return t('model.machineSafe', { value: word });
}
function portTitle(p: ModelChildPort, main: string): string {
  // A PLC output carries the value it takes when its machine faults.
  const safe = p.safe !== undefined ? `${listSep(getLocale())}${safeText(p.safe)}` : '';
  return `${p.name}: ${p.constant ?? (p.path || main)}${safe}`;
}

/** A machine of an array in words, for its cell's tooltip: its path, then its state, "stopped" or its fault. */
function cellText(c: MachineCell): string {
  switch (c.status) {
    case 'running':
      return c.state ? `${c.path}${listSep(getLocale())}${c.state}` : c.path;
    case 'stopped':
      return `${c.path}${listSep(getLocale())}${t('model.machineStopped')}`;
    case 'faulted':
      return `${c.path}${listSep(getLocale())}${t('model.machineFaulted')}: ${c.fault}`;
    default:
      return c.path;
  }
}

@customElement('vitesse-model-machines')
export class ModelMachinesSection extends ModelSectionBase {
  /** The names of the array rows that are open. A new Set on each change, so Lit sees it. */
  @state() private openArrays: ReadonlySet<string> = new Set();
  @state() private view: MachinesView = loadView();
  /** The diagram's wires, from the last measure of its nodes. */
  @state() private wires: Wire[] = [];
  @state() private canvasSize = { w: 0, h: 0 };
  /** The scale that fits the diagram into the tile's width, from the last measure. */
  @state() private scale = 1;

  /** The graph that the last render drew, for the measure that follows it. */
  private graph?: MachineGraph;
  /** The last measure, to set the wires only when they change: a set re-renders, and a render measures. */
  private wireKey = '';
  private rafId = 0;
  private ro?: ResizeObserver;
  private observed?: Element;

  protected sectionHeading(): string {
    return t('model.machines');
  }
  protected sectionClass(): string {
    return 'model-machines-section';
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    cancelAnimationFrame(this.rafId);
    this.ro?.disconnect();
    this.ro = undefined;
    this.observed = undefined;
  }

  protected updated(changed: PropertyValues): void {
    super.updated(changed);
    const diagram = this.querySelector('.mm-diagram');
    // A new diagram after a switch of view: watch its size, so a resize of the tile fits and redraws it.
    if (diagram && diagram !== this.observed) {
      this.ro ??= new ResizeObserver(() => this.scheduleMeasure());
      if (this.observed) this.ro.unobserve(this.observed);
      this.ro.observe(diagram);
      this.observed = diagram;
    }
    if (diagram) this.scheduleMeasure();
  }

  private scheduleMeasure(): void {
    cancelAnimationFrame(this.rafId);
    this.rafId = requestAnimationFrame(() => this.measure());
  }

  /** Fit the canvas to the tile, read each node's box in the canvas, and draw a wire for each edge. */
  private measure(): void {
    const diagram = this.querySelector<HTMLElement>('.mm-diagram');
    const canvas = this.querySelector<HTMLElement>('.mm-canvas');
    if (!diagram || !canvas || !this.graph) return;
    // The canvas's own size does not follow its transform, so the fit is read from it directly.
    const size = { w: canvas.offsetWidth, h: canvas.offsetHeight };
    const scale = Math.max(MIN_SCALE, Math.min(1, diagram.clientWidth / Math.max(1, size.w)));
    // The boxes are read on screen, at the scale on screen now, and taken back to the canvas's own units.
    const shown = this.scale;
    const base = canvas.getBoundingClientRect();
    const boxes = new Map<string, Box>();
    for (const el of canvas.querySelectorAll<HTMLElement>('[data-node]')) {
      const r = el.getBoundingClientRect();
      boxes.set(el.dataset.node ?? '', {
        l: (r.left - base.left) / shown,
        r: (r.right - base.left) / shown,
        t: (r.top - base.top) / shown,
        b: (r.bottom - base.top) / shown,
      });
    }
    // Each wire's bend, and the side of each node that its ends use.
    const plans = this.graph.edges.flatMap((e, index) => {
      const a = boxes.get(e.from);
      const b = boxes.get(e.to);
      if (!a || !b) return [];
      const bend = bendOf(a, b);
      return [{ e, index, a, b, bend, y0: midY(a), y3: midY(b) }];
    });
    // Spread the ends that share a side of a node: in the order of the other end's height, then of
    // the edge, so the wires between 2 nodes keep 1 order at both ends and do not cross.
    type End = { plan: (typeof plans)[number]; from: boolean; other: number };
    const sides = new Map<string, End[]>();
    const add = (key: string, end: End) => sides.set(key, [...(sides.get(key) ?? []), end]);
    for (const plan of plans) {
      add(`${plan.e.from}|${plan.bend === 'back' ? 'l' : 'r'}`, { plan, from: true, other: midY(plan.b) });
      add(`${plan.e.to}|${plan.bend === 'forward' ? 'l' : 'r'}`, { plan, from: false, other: midY(plan.a) });
    }
    for (const ends of sides.values()) {
      ends.sort((x, y) => x.other - y.other || x.plan.index - y.plan.index);
      const box = ends[0].from ? ends[0].plan.a : ends[0].plan.b;
      spread(box, ends.length).forEach((y, k) => {
        if (ends[k].from) ends[k].plan.y0 = y;
        else ends[k].plan.y3 = y;
      });
    }
    const nodes = [...boxes.values()];
    const lanes = new Map<string, number>();
    const live = this.model ? isModelActive(this.model.status) : false;
    const wires = plans.map(({ e, a, b, bend, y0, y3 }) => {
      const x0 = bend === 'back' ? a.l : a.r;
      const x3 = bend === 'forward' ? b.l : b.r;
      return { ...curveOf(x0, y0, x3, y3, bend, nodes, lanes), label: e.label, live: live && e.kind !== 'exposed' };
    });
    const key = JSON.stringify([wires, size, scale]);
    if (key === this.wireKey) return;
    this.wireKey = key;
    this.wires = wires;
    this.canvasSize = size;
    this.scale = scale;
  }

  private setView(id: string): void {
    const view: MachinesView = id === 'list' ? 'list' : 'diagram';
    if (view === this.view) return;
    this.view = view;
    this.wires = [];
    this.wireKey = '';
    try {
      localStorage.setItem(VIEW_KEY, view);
    } catch {
      /* private mode / storage disabled — the view just won't persist */
    }
  }

  private toggleArray(name: string): void {
    const next = new Set(this.openArrays);
    if (!next.delete(name)) next.add(name);
    this.openArrays = next;
  }

  protected renderContent(): unknown {
    const m = this.model;
    if (!m) return nothing;
    if (m.children.length === 0) {
      this.graph = undefined;
      return html`${this.styles()}
        <div class="mm-empty">${t('model.noMachines')}</div>`;
    }
    const active = isModelActive(m.status);
    const graph = machineGraph(m, active);
    this.graph = this.view === 'diagram' ? graph : undefined;
    const tabs: PillTabItem[] = [
      { id: 'diagram', label: t('model.machinesDiagram') },
      { id: 'list', label: t('model.machinesList') },
    ];
    return html`${this.styles()}
      <div class="mm-bar">
        <vitesse-pill-tabs .tabs=${tabs} .active=${this.view} .onChange=${(id: string) => this.setView(id)}></vitesse-pill-tabs>
      </div>
      ${this.view === 'diagram' ? this.renderDiagram(graph, active) : this.renderList(m, active)}`;
  }

  // ── the list ───────────────────────────────────────────────────────────────

  private renderList(m: NonNullable<ModelMachinesSection['model']>, active: boolean) {
    const rows = machineRows(m, active, this.openArrays);
    return html`<div class="mm-list">${rows.map((r) => (r.array ? this.renderArray(r, r.array, active) : this.renderRow(r)))}</div>`;
  }

  /**
   * The wiring of a machine as one phrase, the full paths in its tooltip. A PLC output is in the
   * orange tone, as in the diagram, because it moves the machine.
   */
  private renderPorts(r: MachineRow) {
    const main = this.model?.name ?? '';
    const ports = [
      ...r.inputs.map((p) => ({ p, text: inputText(p, main), plc: false })),
      ...r.outputs.map((p) => ({ p, text: outputText(p), plc: p.path !== '' })),
    ];
    if (ports.length === 0) return nothing;
    return html`<span class="mm-ports" title=${ports.map((x) => portTitle(x.p, main)).join('\n')}
      >${ports.map((x, k) => html`${k > 0 ? listSep(getLocale()) : ''}<span class=${x.plc ? 'mm-port-plc' : ''}>${x.text}</span>`)}</span
    >`;
  }

  /** A fault in the danger tone, on the row's own line, its whole text in the tooltip. */
  private renderFault(fault: string) {
    const text = `${t('model.machineFaulted')}: ${fault}`;
    return html`<span class="mm-fault" title=${text}
      ><vitesse-icon name="triangle-alert" .size=${12}></vitesse-icon><span class="mm-fault-text">${text}</span></span
    >`;
  }

  private renderRow(r: MachineRow) {
    return html`<div class="mm-row">
      <span class="mm-name">${r.name}</span>
      <span class="mm-tag">${r.root ? t('model.machinesRoot') : r.template}</span>
      ${r.fault ? this.renderFault(r.fault) : nothing} ${this.renderPorts(r)}
      <span class="mm-state">${r.state ? statusBadge(r.fault ? 'red' : 'blue', r.state) : nothing}</span>
    </div>`;
  }

  /** An array child: 1 row with its counts, which opens to show each machine. */
  private renderArray(r: MachineRow, a: ArrayRow, active: boolean) {
    const isOpen = a.machines !== undefined;
    const hint = isOpen ? t('model.machinesHide') : t('model.machinesShow');
    // "6 of 8 running" while the model is active. Otherwise only the count: no machine runs.
    const summary = active
      ? statusBadge(a.running > 0 ? 'blue' : 'gray', t('model.machinesRunning', { running: a.running, total: a.count }))
      : statusBadge('gray', t('model.machinesCount', { count: a.count }));
    return html`<button
        type="button"
        class="mm-row mm-toggle"
        aria-expanded=${isOpen ? 'true' : 'false'}
        title=${hint}
        @click=${() => this.toggleArray(r.name)}
      >
        <vitesse-icon class="mm-chev" name=${isOpen ? 'chevron-down' : 'chevron-right'} .size=${12}></vitesse-icon>
        <span class="mm-name">${r.name}</span>
        <span class="mm-tag">${r.template} × ${a.count}</span>
        ${this.renderPorts(r)}
        <span class="mm-state">${summary}${a.faulted > 0 ? statusBadge('red', t('model.machinesFaulted', { faulted: a.faulted })) : nothing}</span>
      </button>
      ${a.machines ? (a.layout === 'grid' ? html`<div class="mm-sub">${this.renderGrid(a.machines)}</div>` : this.renderMachineRows(a.machines)) : nothing}`;
  }

  /** A small array: a row for each machine, under its array's row. */
  private renderMachineRows(cells: MachineCell[]) {
    return cells.map(
      (c) => html`<div class="mm-row mm-machine">
        <span class="mm-name">${c.path}</span>
        ${c.status === 'faulted' ? this.renderFault(c.fault) : nothing}
        <span class="mm-state">
          ${c.status === 'running' && c.state ? statusBadge('blue', c.state) : nothing}
          ${c.status === 'stopped' ? statusBadge('gray', t('model.machineStopped')) : nothing}
        </span>
      </div>`,
    );
  }

  /** A large array: a small cell for each machine, in the color of its status. The tooltip gives its path and its state. */
  private renderGrid(cells: MachineCell[]) {
    // The faults are read under the grid, one a line: a red cell alone says which, not why.
    const faulted = cells.filter((c) => c.status === 'faulted');
    return html`<div class="mm-grid" role="list">
        ${cells.map((c) => {
          const text = cellText(c);
          return html`<span class="mm-cell mm-${c.status}" role="listitem" title=${text} aria-label=${text}>${c.index}</span>`;
        })}
      </div>
      ${faulted.length > 0
        ? html`<div class="mm-faults">
            ${faulted.map((c) => {
              const text = `${c.path}: ${c.fault}`;
              return html`<span class="mm-fault-line" title=${text}>${text}</span>`;
            })}
          </div>`
        : nothing}`;
  }

  // ── the diagram ────────────────────────────────────────────────────────────

  private renderDiagram(g: MachineGraph, active: boolean) {
    const column = (nodes: GraphMachine[]) =>
      nodes.length > 0 ? html`<div class="mm-col">${nodes.map((n) => this.renderNode(n, active))}</div>` : nothing;
    const { w, h } = this.canvasSize;
    // The scaled canvas still takes its full size in the layout, so the frame is given the scaled height.
    const fitted = this.scale < 1 && h > 0 ? `height: ${Math.ceil(h * this.scale)}px` : '';
    return html`<div class="mm-diagram ${this.scale <= MIN_SCALE ? 'mm-scroll' : ''}" style=${fitted}>
      <div class="mm-canvas" style=${this.scale < 1 ? `transform: scale(${this.scale})` : ''}>
        <svg class="mm-wires" width=${w} height=${h} aria-hidden="true">
          ${this.wires.map((x) => svg`<path class="mm-wire" d=${x.d}></path>`)}
          ${this.wires.filter((x) => x.live).map((x) => svg`<path class="mm-wire-flow" d=${x.d}></path>`)}
          ${this.wires.map((x) => svg`<text class="mm-wire-label" x=${f1(x.lx)} y=${f1(x.ly)} text-anchor="middle">${x.label}</text>`)}
        </svg>
        ${g.inputs.length > 0
          ? html`<div class="mm-col">
              <span class="mm-sub-label">${t('model.machinesInputsLabel')}</span>
              ${g.inputs.map(
                (i) => html`<div class="mm-io" data-node=${i.id} title=${i.path}>
                  <span class="mm-io-name">${shortPath(i.path)}</span>
                </div>`,
              )}
            </div>`
          : nothing}
        ${column(g.before)}
        <div class="mm-col">${this.renderNode(g.root, active)}</div>
        ${column(g.after)}
        ${g.outputs.length > 0
          ? html`<div class="mm-col">
              <span class="mm-sub-label">${t('model.machinesOutputsLabel')}</span>
              ${g.outputs.map((o) => this.renderOutput(o))}
            </div>`
          : nothing}
      </div>
    </div>`;
  }

  /**
   * A published output, with "Shown in app" under its name. A PLC output, in the orange tone,
   * because it moves the machine, with what it is set to when the machine stops.
   */
  private renderOutput(o: GraphOutput) {
    if (o.kind === 'exposed') {
      const note = t('model.machinesExposed');
      return html`<div class="mm-io" data-node=${o.id} title=${`${o.name}${listSep(getLocale())}${note}`}>
        <span class="mm-io-text"><span class="mm-io-name">${o.name}</span><span class="mm-io-note">${note}</span></span>
      </div>`;
    }
    const safe = o.safe !== undefined ? safeText(o.safe) : '';
    return html`<div class="mm-io mm-io-plc" data-node=${o.id} title=${safe ? `${o.path}${listSep(getLocale())}${safe}` : o.path}>
      <span class="mm-io-text"
        ><span class="mm-io-name">${shortPath(o.path)}</span>${safe ? html`<span class="mm-io-note">${safe}</span>` : nothing}</span
      >
    </div>`;
  }

  /**
   * The root or a child, as the flows canvas's model card: its name, its active
   * states (or its fault) in the small mono line, its settings (constants) under them, and its
   * template last. The whole fault is in the tooltip.
   */
  private renderNode(n: GraphMachine, active: boolean) {
    const kind = n.root ? t('model.machinesRoot') : n.array ? `${n.template} × ${n.array.count}` : n.template;
    const consts = n.constants.map((c) => `${c.name} = ${c.value}`);
    const title = [n.name, kind, ...consts, ...(n.fault ? [`${t('model.machineFaulted')}: ${n.fault}`] : [])].join('\n');
    const line = n.fault
      ? `${t('model.machineFaulted')}: ${n.fault}`
      : n.array
        ? active
          ? t('model.machinesRunning', { running: n.array.running, total: n.array.count })
          : t('model.machinesCount', { count: n.array.count })
        : n.states.join(', ');
    return html`<div class="mm-node ${n.root ? 'mm-node-root' : ''}" data-node=${n.id} title=${title}>
      <span class="mm-node-name">${n.name}</span>
      ${line
        ? html`<span class="mm-node-state ${n.fault ? 'err' : ''}"
            >${line}${n.array && n.array.faulted > 0
              ? html`${listSep(getLocale())}<span class="err">${t('model.machinesFaulted', { faulted: n.array.faulted })}</span>`
              : nothing}</span
          >`
        : nothing}
      ${consts.length > 0 ? html`<span class="mm-node-consts">${consts.join(listSep(getLocale()))}</span>` : nothing}
      ${n.array ? this.renderGrid(n.array.machines) : nothing}
      <span class="mm-node-kind">${kind}</span>
    </div>`;
  }

  private styles() {
    return html`<style>
      /* THE BAR above both views: the Diagram / List pills, on the right. */
      .model-machines-section .mm-bar {
        display: flex;
        justify-content: flex-end;
        margin-bottom: 10px;
      }
      .model-machines-section .mm-empty {
        font-size: 12px;
        color: var(--text-secondary);
      }

      /* THE LIST: the bindings panel's row system. One bordered box, hairline-separated 34px rows,
         one line per machine, always. One mono size; the only axis is tone — the name lit, the
         template and the wiring in --text-secondary. */
      .model-machines-section .mm-list {
        display: flex;
        flex-direction: column;
        border: 1px solid var(--border-color);
        border-radius: 8px;
        overflow: hidden;
      }
      .model-machines-section .mm-row {
        display: flex;
        align-items: center;
        gap: 8px;
        height: 34px;
        padding: 0 6px 0 10px;
        border-bottom: 1px solid var(--border-color);
        font-size: 12px;
        font-family: 'SF Mono', ui-monospace, monospace;
        box-sizing: border-box;
      }
      .model-machines-section .mm-row:last-child {
        border-bottom: none;
      }
      /* An array's row is a button that opens it: no button chrome, the row's own look. */
      .model-machines-section .mm-toggle {
        width: 100%;
        border-top: 0;
        border-left: 0;
        border-right: 0;
        background: none;
        color: inherit;
        text-align: left;
        cursor: pointer;
      }
      .model-machines-section .mm-chev {
        color: var(--text-secondary);
        flex-shrink: 0;
      }
      /* The machines of an open array, a step in from their array's row. */
      .model-machines-section .mm-machine {
        padding-left: 30px;
      }
      .model-machines-section .mm-name {
        flex: 0 0 auto;
        color: var(--text-primary);
        white-space: nowrap;
      }
      .model-machines-section .mm-tag {
        flex: 0 0 auto;
        color: var(--text-secondary);
        white-space: nowrap;
      }
      /* The wiring absorbs all the truncation, so the name and the state always survive. */
      .model-machines-section .mm-ports {
        flex: 0 1 auto;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: var(--text-secondary);
      }
      .model-machines-section .mm-port-plc {
        color: var(--orange);
      }
      .model-machines-section .mm-state {
        margin-left: auto; /* the state owns the right edge; the sentence hugs the left */
        display: flex;
        align-items: center;
        gap: 4px;
        flex-shrink: 0;
      }
      /* A fault: the danger tone the rest of the app uses for a broken state, on the row's own line. */
      .model-machines-section .mm-fault {
        display: flex;
        align-items: center;
        gap: 6px;
        flex: 0 1 auto;
        min-width: 0;
        color: var(--red);
      }
      .model-machines-section .mm-fault vitesse-icon {
        flex-shrink: 0;
      }
      .model-machines-section .mm-fault-text {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      /* NARROW → the same line, stripped to the name and the state. Sized against the section's
         container, so it answers the tile being dragged narrow, not the viewport. */
      @container (max-width: 420px) {
        .model-machines-section .mm-tag,
        .model-machines-section .mm-ports {
          display: none;
        }
      }
      /* A large array open in the list: its grid under its row. */
      .model-machines-section .mm-sub {
        padding: 8px 10px 8px 30px;
        border-bottom: 1px solid var(--border-color);
      }
      .model-machines-section .mm-sub:last-child {
        border-bottom: none;
      }
      /* A cell for each machine of a large array: its index, in the color of its status. */
      .model-machines-section .mm-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(24px, 1fr));
        gap: 3px;
      }
      .model-machines-section .mm-cell {
        height: 18px;
        border-radius: 4px;
        font-size: 10px;
        line-height: 18px;
        text-align: center;
        font-family: 'SF Mono', ui-monospace, monospace;
        color: var(--text-secondary);
        background: color-mix(in srgb, var(--text-secondary) 12%, transparent);
        cursor: default;
      }
      .model-machines-section .mm-cell.mm-running {
        color: var(--green);
        background: color-mix(in srgb, var(--green) 16%, transparent);
      }
      .model-machines-section .mm-cell.mm-faulted {
        color: var(--red);
        background: color-mix(in srgb, var(--red) 16%, transparent);
      }

      /* THE DIAGRAM: the flows canvas's graph. The canvas is as wide as its columns, scaled down to
         the tile's width; the wires are an SVG under the nodes, sized to the canvas by the measure. */
      .model-machines-section .mm-diagram {
        overflow: hidden;
      }
      .model-machines-section .mm-diagram.mm-scroll {
        overflow: auto;
      }
      .model-machines-section .mm-canvas {
        position: relative;
        display: flex;
        align-items: center;
        gap: 64px;
        width: max-content;
        padding: 18px 4px 8px;
        transform-origin: 0 0;
      }
      .model-machines-section .mm-wires {
        position: absolute;
        left: 0;
        top: 0;
        z-index: 0;
        overflow: visible;
        pointer-events: none;
      }
      .model-machines-section .mm-wire {
        fill: none;
        stroke: var(--border-color);
        stroke-width: 1.5;
      }
      /* A live edge, as on the flows canvas: a blue dash that runs along the wire. */
      .model-machines-section .mm-wire-flow {
        fill: none;
        stroke: var(--blue);
        stroke-width: 2;
        stroke-linecap: round;
        stroke-dasharray: 5 11;
        animation: mm-wire-flow 0.9s linear infinite;
      }
      @keyframes mm-wire-flow {
        to {
          stroke-dashoffset: -16;
        }
      }
      @media (prefers-reduced-motion: reduce) {
        .model-machines-section .mm-wire-flow {
          animation: none;
        }
      }
      .model-machines-section .mm-col {
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 16px;
      }
      /* The flows canvas's sub-label: small caps in the secondary tone. */
      .model-machines-section .mm-sub-label {
        font-size: 10px;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: var(--text-secondary);
      }
      /* A machine: the flows canvas's model card. Opaque, so the wires under it do not show through. */
      .model-machines-section .mm-node {
        position: relative;
        z-index: 1;
        display: flex;
        flex-direction: column;
        gap: 6px;
        width: 184px;
        padding: 11px 12px;
        border: 1px solid var(--border-color);
        border-radius: 10px;
        background: var(--bg-primary);
        box-sizing: border-box;
      }
      .model-machines-section .mm-node-root {
        border-color: var(--blue);
      }
      /* A long name takes a second line, not an ellipsis. */
      .model-machines-section .mm-node-name {
        font-size: 12px;
        color: var(--text-primary);
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow-wrap: anywhere;
      }
      .model-machines-section .mm-node-state {
        font-size: 10px;
        color: var(--text-secondary);
        font-family: 'SF Mono', ui-monospace, monospace;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .model-machines-section .mm-node-consts {
        font-size: 10px;
        color: var(--text-secondary);
        font-family: 'SF Mono', ui-monospace, monospace;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      /* The faults of a large array, under its grid: one a line, in the danger tone. */
      .model-machines-section .mm-faults {
        display: flex;
        flex-direction: column;
        gap: 2px;
        margin-top: 6px;
        min-width: 0;
      }
      .model-machines-section .mm-fault-line {
        font-size: 10px;
        font-family: 'SF Mono', ui-monospace, monospace;
        color: var(--red);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .model-machines-section .mm-node-state .err,
      .model-machines-section .mm-node-state.err {
        color: var(--red);
      }
      .model-machines-section .mm-node-kind {
        font-size: 9px;
        letter-spacing: 0.04em;
        color: var(--text-secondary);
        opacity: 0.75;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      /* An outside input, a PLC output, or a published output: a smaller card of the same kind. */
      .model-machines-section .mm-io {
        position: relative;
        z-index: 1;
        display: flex;
        align-items: center;
        gap: 6px;
        max-width: 168px;
        padding: 7px 10px;
        border: 1px solid var(--border-color);
        border-radius: 8px;
        background: var(--bg-primary);
        box-sizing: border-box;
        color: var(--text-secondary);
      }
      .model-machines-section .mm-io-text {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
      }
      .model-machines-section .mm-io-name {
        font-size: 12px;
        color: var(--text-primary);
        font-family: 'SF Mono', ui-monospace, monospace;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .model-machines-section .mm-io-note {
        font-size: 10px;
        color: var(--text-secondary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      /* A PLC output moves the machine: a light orange touch on its border and note. */
      .model-machines-section .mm-io-plc {
        border-color: color-mix(in srgb, var(--orange) 45%, var(--border-color));
      }
      .model-machines-section .mm-io-plc .mm-io-note {
        color: var(--orange);
      }
      /* What a wire carries: small text in the secondary tone, cut out of the wires under it. */
      .model-machines-section .mm-wire-label {
        font-size: 10px;
        font-family: 'SF Mono', ui-monospace, monospace;
        fill: var(--text-secondary);
        paint-order: stroke;
        stroke: var(--card-bg);
        stroke-width: 4px;
        stroke-linejoin: round;
      }
    </style>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-model-machines': ModelMachinesSection;
  }
}
